import { useCallback } from 'react';
import {
  useInfiniteQuery,
  useQuery,
  useMutation,
  useQueryClient,
  type InfiniteData,
  type QueryClient,
} from '@tanstack/react-query';
import { resolveChecklistDone } from '@peles/shared';
import type {
  Timeline,
  CreateTimelineInput,
  UpdateTimelineInput,
  CreateTimelineSectionInput,
  UpdateTimelineSectionInput,
  AddTimelineEntryInput,
  UpdateTimelineEntryInput,
  Checklist,
  ChecklistItem,
  CreateChecklistInput,
  UpdateChecklistInput,
  CreateChecklistSectionInput,
  UpdateChecklistSectionInput,
  CreateChecklistItemInput,
  UpdateChecklistItemInput,
  Kibuddim,
  CreateKibuddimInput,
  UpdateKibuddimInput,
  CreateKibuddimSectionInput,
  UpdateKibuddimSectionInput,
  CreateKibuddimRoleInput,
  UpdateKibuddimRoleInput,
  ChecklistItemStatus,
  TaskQuery,
  TaskListResponse,
  BulkTaskInput,
  AddTimelineVendorTimesInput,
} from '@peles/shared';
import * as eventOpsApi from '../api/event-ops';

// ─── Timeline Hooks ─────────────────────────────────────────────────────────

export function useTimelines(eventId?: string, { enabled = true }: { enabled?: boolean } = {}) {
  return useQuery<Timeline[]>({
    queryKey: ['timelines', eventId ?? 'all'],
    queryFn: () => eventOpsApi.getTimelines(eventId),
    enabled,
  });
}

export function useTimeline(id: string) {
  return useQuery<Timeline>({
    queryKey: ['timelines', 'detail', id],
    queryFn: () => eventOpsApi.getTimeline(id),
    enabled: !!id,
  });
}

export function useCreateTimeline() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateTimelineInput) => eventOpsApi.createTimeline(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['timelines'] });
    },
  });
}

export function useUpdateTimeline() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, updates }: { id: string; updates: UpdateTimelineInput }) =>
      eventOpsApi.updateTimeline(id, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['timelines'] });
    },
  });
}

export function useDeleteTimeline() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => eventOpsApi.deleteTimeline(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['timelines'] });
    },
  });
}

export function useCloneTimeline() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: { id: string; eventId?: string; title?: string }) =>
      eventOpsApi.cloneTimeline(id, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['timelines'] });
    },
  });
}

export function useAddTimelineSection() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ timelineId, ...input }: CreateTimelineSectionInput & { timelineId: string }) =>
      eventOpsApi.addTimelineSection(timelineId, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['timelines'] });
    },
  });
}

export function useUpdateTimelineSection() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      sectionId,
      updates,
    }: {
      sectionId: string;
      updates: UpdateTimelineSectionInput;
    }) => eventOpsApi.updateTimelineSection(sectionId, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['timelines'] });
    },
  });
}

export function useDeleteTimelineSection() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (sectionId: string) => eventOpsApi.deleteTimelineSection(sectionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['timelines'] });
    },
  });
}

export function useAddTimelineEntry() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ sectionId, ...input }: AddTimelineEntryInput & { sectionId: string }) =>
      eventOpsApi.addTimelineEntry(sectionId, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['timelines'] });
    },
  });
}

export function useUpdateTimelineEntry() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ entryId, updates }: { entryId: string; updates: UpdateTimelineEntryInput }) =>
      eventOpsApi.updateTimelineEntry(entryId, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['timelines'] });
    },
  });
}

export function useDeleteTimelineEntry() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (entryId: string) => eventOpsApi.deleteTimelineEntry(entryId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['timelines'] });
    },
  });
}

/** Fetches every timeline query again — the editor asks once its queued reorders are done. */
export function useRefreshTimelines() {
  const queryClient = useQueryClient();
  return useCallback(
    () => queryClient.invalidateQueries({ queryKey: ['timelines'] }),
    [queryClient],
  );
}

/** A drop of a section: the whole new order, saved in one request. */
export function useReorderTimelineSections() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      timelineId,
      sectionIds,
      expected,
    }: {
      timelineId: string;
      sectionIds: string[];
      expected: string[];
    }) => eventOpsApi.reorderTimelineSections(timelineId, sectionIds, expected),
    onSettled: () => queryClient.invalidateQueries({ queryKey: ['timelines'] }),
  });
}

/**
 * A drop of an entry: the section it landed in, and the one it left when it
 * crossed — one request, one transaction, so a move is saved whole or not at all.
 */
export function useReorderTimelineEntries() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      timelineId,
      sections,
    }: {
      timelineId: string;
      sections: eventOpsApi.TimelineEntryOrder[];
    }) => eventOpsApi.reorderTimelineEntries(timelineId, sections),
    onSettled: () => queryClient.invalidateQueries({ queryKey: ['timelines'] }),
  });
}

/**
 * The bookings' day-of times offered to one timeline. Under `timelines`, so
 * every timeline change refreshes it; a booking's time changing refreshes it
 * through `DEPENDENT_QUERIES` (entity links, vendors).
 */
export const timelineVendorTimesKey = (timelineId: string) =>
  ['timelines', 'vendor-times', timelineId] as const;

export function useTimelineVendorTimes(timelineId: string | undefined, enabled = true) {
  return useQuery({
    queryKey: timelineVendorTimesKey(timelineId ?? ''),
    queryFn: () => eventOpsApi.getTimelineVendorTimes(timelineId!),
    enabled: enabled && !!timelineId,
  });
}

/** Adds bookings' times to a timeline — nothing already on it is added twice. */
export function useAddTimelineVendorTimes() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ timelineId, ...input }: AddTimelineVendorTimesInput & { timelineId: string }) =>
      eventOpsApi.addTimelineVendorTimes(timelineId, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['timelines'] });
    },
  });
}

// ─── Checklist Hooks ────────────────────────────────────────────────────────

/** Every query that shows tasks: the lists, and tasks across lists. */
const TASK_QUERY_ROOTS = [['checklists'], ['tasks'], ['templates'], ['item-tasks']] as const;

/**
 * A change to any task can move every view of tasks — refresh them together.
 * That includes the Templates page: a checklist template created or renamed
 * on the tasks screen is listed there under its own query, and an item's own
 * tasks (`item-tasks`), which are ordinary tasks shown in the item editor.
 */
export function invalidateTasks(queryClient: QueryClient) {
  return Promise.all(
    TASK_QUERY_ROOTS.map((root) => queryClient.invalidateQueries({ queryKey: root })),
  );
}

/** One event's lists, every event's, or — with `templates` — the checklist templates. */
export function useChecklists(eventId?: string, options: { templates?: boolean } = {}) {
  return useQuery<Checklist[]>({
    queryKey: ['checklists', options.templates ? 'templates' : (eventId ?? 'all')],
    queryFn: () =>
      options.templates
        ? eventOpsApi.getChecklists(undefined, { templates: true })
        : eventOpsApi.getChecklists(eventId),
  });
}

export function useChecklist(id: string) {
  return useQuery<Checklist>({
    queryKey: ['checklists', 'detail', id],
    queryFn: () => eventOpsApi.getChecklist(id),
    enabled: !!id,
  });
}

export function useCreateChecklist() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateChecklistInput) => eventOpsApi.createChecklist(data),
    onSuccess: () => invalidateTasks(queryClient),
  });
}

export function useUpdateChecklist() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, updates }: { id: string; updates: UpdateChecklistInput }) =>
      eventOpsApi.updateChecklist(id, updates),
    onSuccess: () => invalidateTasks(queryClient),
  });
}

export function useDeleteChecklist() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => eventOpsApi.deleteChecklist(id),
    onSuccess: () => invalidateTasks(queryClient),
  });
}

export function useCloneChecklist() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: { id: string; eventId?: string; title?: string }) =>
      eventOpsApi.cloneChecklist(id, body),
    onSuccess: () => invalidateTasks(queryClient),
  });
}

export function useAddChecklistSection() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      checklistId,
      ...input
    }: CreateChecklistSectionInput & { checklistId: string }) =>
      eventOpsApi.addChecklistSection(checklistId, input),
    onSuccess: () => invalidateTasks(queryClient),
  });
}

export function useUpdateChecklistSection() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      sectionId,
      updates,
    }: {
      sectionId: string;
      updates: UpdateChecklistSectionInput;
    }) => eventOpsApi.updateChecklistSection(sectionId, updates),
    onSuccess: () => invalidateTasks(queryClient),
  });
}

export function useDeleteChecklistSection() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (sectionId: string) => eventOpsApi.deleteChecklistSection(sectionId),
    onSuccess: () => invalidateTasks(queryClient),
  });
}

export function useAddChecklistItem() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ sectionId, ...input }: CreateChecklistItemInput & { sectionId: string }) =>
      eventOpsApi.addChecklistItem(sectionId, input),
    onSuccess: () => invalidateTasks(queryClient),
  });
}

export function useUpdateChecklistItem() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ itemId, updates }: { itemId: string; updates: UpdateChecklistItemInput }) =>
      eventOpsApi.updateChecklistItem(itemId, updates),
    onSuccess: () => invalidateTasks(queryClient),
  });
}

export function useDeleteChecklistItem() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (itemId: string) => eventOpsApi.deleteChecklistItem(itemId),
    onSuccess: () => invalidateTasks(queryClient),
  });
}

type DoneFields = { isDone: boolean; status: ChecklistItemStatus };

/**
 * Sets one task's done state in every cached view of tasks — the lists, a
 * page of tasks, and pages loaded with "Show more" — touching nothing else,
 * so another task's pending tick is never disturbed.
 */
function patchTaskEverywhere(
  queryClient: QueryClient,
  itemId: string,
  next: (item: DoneFields) => DoneFields,
) {
  const patch = <T extends { id: string } & DoneFields>(item: T): T =>
    item.id === itemId ? { ...item, ...next(item) } : item;
  const patchPage = (page: TaskListResponse): TaskListResponse => ({
    ...page,
    items: page.items.map(patch),
  });
  queryClient.setQueriesData<Checklist[]>({ queryKey: ['checklists'] }, (lists) =>
    Array.isArray(lists)
      ? lists.map((list) => ({
          ...list,
          sections: list.sections.map((section) => ({
            ...section,
            items: section.items.map(patch),
          })),
        }))
      : lists,
  );
  queryClient.setQueriesData<TaskListResponse | InfiniteData<TaskListResponse>>(
    { queryKey: ['tasks'] },
    (data) => {
      if (!data) return data;
      if ('pages' in data) return { ...data, pages: data.pages.map(patchPage) };
      return Array.isArray(data.items) ? patchPage(data) : data;
    },
  );
  queryClient.setQueriesData<ChecklistItem[]>({ queryKey: ['item-tasks'] }, (items) =>
    Array.isArray(items) ? items.map(patch) : items,
  );
}

/** A task's done state as any cached view holds it, before a change. */
function findTaskDone(queryClient: QueryClient, itemId: string): DoneFields | null {
  for (const [, lists] of queryClient.getQueriesData<Checklist[]>({ queryKey: ['checklists'] })) {
    for (const list of Array.isArray(lists) ? lists : []) {
      for (const section of list.sections) {
        const item = section.items.find((i) => i.id === itemId);
        if (item) return { isDone: item.isDone, status: item.status };
      }
    }
  }
  for (const [, data] of queryClient.getQueriesData<
    TaskListResponse | InfiniteData<TaskListResponse>
  >({ queryKey: ['tasks'] })) {
    const pages = !data ? [] : 'pages' in data ? data.pages : [data];
    for (const page of pages) {
      const item = page.items?.find((i) => i.id === itemId);
      if (item) return { isDone: item.isDone, status: item.status };
    }
  }
  for (const [, items] of queryClient.getQueriesData<ChecklistItem[]>({
    queryKey: ['item-tasks'],
  })) {
    const item = Array.isArray(items) ? items.find((i) => i.id === itemId) : undefined;
    if (item) return { isDone: item.isDone, status: item.status };
  }
  return null;
}

export function useToggleChecklistItem() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ itemId, isDone }: { itemId: string; isDone: boolean }) =>
      eventOpsApi.toggleChecklistItem(itemId, isDone),
    // The tick shows at once. A failed save puts back that one task only —
    // restoring whole snapshots would also undo another task ticked meanwhile.
    onMutate: async ({ itemId, isDone }) => {
      await Promise.all(
        TASK_QUERY_ROOTS.map((root) => queryClient.cancelQueries({ queryKey: root })),
      );
      const before = findTaskDone(queryClient, itemId);
      patchTaskEverywhere(queryClient, itemId, (item) => ({
        ...item,
        ...resolveChecklistDone({ isDone }, item),
      }));
      return { itemId, before };
    },
    onError: (_err, _vars, context) => {
      const before = context?.before;
      if (context && before) patchTaskEverywhere(queryClient, context.itemId, () => before);
    },
    onSettled: () => invalidateTasks(queryClient),
  });
}

export function useChecklistItemsForCalendar(year: number, month: number) {
  const dateFrom = new Date(year, month, 1).toISOString().slice(0, 10);
  const dateTo = new Date(year, month + 1, 0).toISOString().slice(0, 10);
  return useQuery({
    queryKey: ['checklist-items-calendar', year, month],
    queryFn: () => eventOpsApi.getChecklistItemsForCalendar({ dateFrom, dateTo }),
  });
}

export function useChecklistItemsForDateRange(dateFrom: string, dateTo: string, enabled = true) {
  return useQuery({
    queryKey: ['checklist-items-calendar-range', dateFrom, dateTo],
    queryFn: () => eventOpsApi.getChecklistItemsForCalendar({ dateFrom, dateTo }),
    enabled,
  });
}

// ─── Kibuddim Hooks ────────────────────────────────────────────────────────

export function useKibuddimList(eventId?: string) {
  return useQuery<Kibuddim[]>({
    queryKey: ['kibuddim', eventId ?? 'all'],
    queryFn: () => eventOpsApi.getKibuddimList(eventId),
  });
}

export function useKibuddimSingle(id: string) {
  return useQuery<Kibuddim>({
    queryKey: ['kibuddim', 'detail', id],
    queryFn: () => eventOpsApi.getKibuddimSingle(id),
    enabled: !!id,
  });
}

export function useCreateKibuddim() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateKibuddimInput) => eventOpsApi.createKibuddim(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['kibuddim'] });
    },
  });
}

export function useUpdateKibuddim() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, updates }: { id: string; updates: UpdateKibuddimInput }) =>
      eventOpsApi.updateKibuddim(id, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['kibuddim'] });
    },
  });
}

export function useDeleteKibuddim() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => eventOpsApi.deleteKibuddim(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['kibuddim'] });
    },
  });
}

export function useCloneKibuddim() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: { id: string; eventId?: string; title?: string }) =>
      eventOpsApi.cloneKibuddim(id, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['kibuddim'] });
    },
  });
}

export function useAddKibuddimSection() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ kibuddimId, ...input }: CreateKibuddimSectionInput & { kibuddimId: string }) =>
      eventOpsApi.addKibuddimSection(kibuddimId, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['kibuddim'] });
    },
  });
}

export function useUpdateKibuddimSection() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      sectionId,
      updates,
    }: {
      sectionId: string;
      updates: UpdateKibuddimSectionInput;
    }) => eventOpsApi.updateKibuddimSection(sectionId, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['kibuddim'] });
    },
  });
}

export function useDeleteKibuddimSection() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (sectionId: string) => eventOpsApi.deleteKibuddimSection(sectionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['kibuddim'] });
    },
  });
}

export function useAddKibuddimRole() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ sectionId, ...input }: CreateKibuddimRoleInput & { sectionId: string }) =>
      eventOpsApi.addKibuddimRole(sectionId, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['kibuddim'] });
    },
  });
}

export function useUpdateKibuddimRole() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ roleId, updates }: { roleId: string; updates: UpdateKibuddimRoleInput }) =>
      eventOpsApi.updateKibuddimRole(roleId, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['kibuddim'] });
    },
  });
}

export function useDeleteKibuddimRole() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (roleId: string) => eventOpsApi.deleteKibuddimRole(roleId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['kibuddim'] });
    },
  });
}

// ─── Task Hooks (items across lists) ─────────────────────────────────────────

/** Page size for tasks loaded a page at a time. */
export const TASK_PAGE_SIZE = 50;

/**
 * Tasks a page at a time, each page asked for by offset — "Show more" adds
 * the next page rather than asking for one ever-larger page, which the API
 * caps at 200.
 */
export function useTaskPages(query: Omit<TaskQuery, 'limit' | 'offset'>, enabled = true) {
  return useInfiniteQuery({
    queryKey: ['tasks', 'pages', query],
    queryFn: ({ pageParam }) =>
      eventOpsApi.listTasks({ ...query, limit: TASK_PAGE_SIZE, offset: pageParam }),
    initialPageParam: 0,
    getNextPageParam: (last, pages) => {
      const loaded = pages.reduce((n, p) => n + p.items.length, 0);
      return last.items.length > 0 && loaded < last.total ? loaded : undefined;
    },
    enabled,
    // While a changed search or assignee loads, the last page stays on screen
    // (marked `isPlaceholderData`, and not actionable). A different event,
    // status or due filter starts from a skeleton instead: the page groups by
    // status and counts by due date, so the old tasks would appear under the
    // new headings — open tasks under "Done", with the old count.
    placeholderData: (previous, previousQuery) =>
      sameShape(previousQuery?.queryKey[2], query) ? previous : undefined,
  });
}

/** Whether an earlier task query was for the same event (or both across events). */
function sameEvent(previous: unknown, query: { eventId?: string }): boolean {
  return (previous as { eventId?: string } | undefined)?.eventId === query.eventId;
}

/** Same event, status and due filter: what the page's groups and counts are made of. */
function sameShape(previous: unknown, query: Omit<TaskQuery, 'limit' | 'offset'>): boolean {
  const prev = previous as Omit<TaskQuery, 'limit' | 'offset'> | undefined;
  return (
    sameEvent(prev, query) &&
    (prev?.status ?? 'open') === (query.status ?? 'open') &&
    [...(prev?.due ?? [])].sort().join(',') === [...(query.due ?? [])].sort().join(',')
  );
}

export function useTasks(query: TaskQuery, enabled = true) {
  return useQuery<TaskListResponse>({
    queryKey: ['tasks', query],
    queryFn: () => eventOpsApi.listTasks(query),
    enabled,
    placeholderData: (previous, previousQuery) =>
      sameEvent(previousQuery?.queryKey[1], query) ? previous : undefined,
  });
}

/** How many of my open tasks are overdue or due today — the sidebar's badge. */
export function useMyTaskCounts(date: string, enabled = true) {
  return useQuery<TaskListResponse>({
    queryKey: ['tasks', 'mine', date],
    queryFn: () => eventOpsApi.listTasks({ assignee: ['me'], date, limit: 0 }),
    enabled,
    staleTime: 60_000,
    refetchInterval: 5 * 60_000,
  });
}

export function useBulkUpdateTasks() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: BulkTaskInput) => eventOpsApi.bulkUpdateTasks(input),
    onSettled: () => invalidateTasks(queryClient),
  });
}

export function useReorderChecklistSections() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ checklistId, sectionIds }: { checklistId: string; sectionIds: string[] }) =>
      eventOpsApi.reorderChecklistSections(checklistId, sectionIds),
    onSettled: () => invalidateTasks(queryClient),
  });
}

export function useReorderChecklistItems() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ sectionId, itemIds }: { sectionId: string; itemIds: string[] }) =>
      eventOpsApi.reorderChecklistItems(sectionId, itemIds),
    onSettled: () => invalidateTasks(queryClient),
  });
}
